/**
 * PlansView Component
 * Displays plan comparison with feature matrix
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CheckOutlined as CheckIcon,
  CloseOutlined as CloseIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { planTiers, planFeatures, getFeaturesByCategory } from '@/data/plansData';
import { PlanTier, FeatureValue } from '@/types/planTypes';
import { PlanChangeDialog } from '@/components/PlanChangeDialog/PlanChangeDialog';
import { SupportChat } from '@/components/SupportChat';

interface PlansViewProps {
  currentPlan?: PlanTier;
  onPlanSelect?: (planId: PlanTier) => void;
}

export const PlansView: React.FC<PlansViewProps> = ({
  currentPlan = 'professional',
  onPlanSelect
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annually'>('annually');
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanTier | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const featuresByCategory = getFeaturesByCategory();

  const renderFeatureValue = (value: FeatureValue) => {
    if (typeof value === 'boolean') {
      return value ? (
        <CheckIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
      ) : (
        <CloseIcon sx={{ color: theme.palette.text.disabled, fontSize: 20 }} />
      );
    }
    return (
      <Typography variant="body2" sx={{ fontSize: '13px' }}>
        {value}
      </Typography>
    );
  };

  const handlePlanAction = (planId: PlanTier, action: string) => {
    if (action === 'contact') {
      // Open contact sales dialog or navigate to contact page
      window.location.href = 'mailto:sales@intakeai.com';
    } else if (action !== 'current') {
      // Open confirmation dialog
      setSelectedPlan(planId);
      setChangeDialogOpen(true);
    }
  };

  const handleConfirmPlanChange = () => {
    if (selectedPlan) {
      onPlanSelect?.(selectedPlan);
      setChangeDialogOpen(false);
      setSelectedPlan(null);
    }
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        p: isMobile ? 2 : 4
      }}
    >
      <Box sx={{ maxWidth: 900, width: '100%', textAlign: 'left' }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'left' }}>
          <Typography variant="h4" fontWeight={600} gutterBottom sx={{ fontSize: '28px', textAlign: 'left' }}>
            Plans
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '14px', textAlign: 'left' }}>
            Designed for every stage of your journey. If you couldn't find something,{' '}
            <Typography
              component="span"
              sx={{
                fontSize: '14px',
                color: 'text.secondary',
                textDecoration: 'underline',
                textDecorationColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                textUnderlineOffset: '3px',
                cursor: 'pointer',
                '&:hover': {
                  color: 'text.primary',
                  textDecorationColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                }
              }}
              onClick={() => setChatOpen(true)}
            >
              message us →
            </Typography>
          </Typography>

          {/* Current Plan Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: '4px',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'transparent',
              mb: 2
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 400 }}>
              Current
            </Typography>
            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '12px' }}>
              Renews Jul 09, 2026
            </Typography>
          </Box>

          {/* Plan Header */}
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 2.5,
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'transparent'
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
              Pro
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
              $69.00 per user/month, billed annually ·{' '}
              <Typography
                component="span"
                sx={{ color: theme.palette.primary.main, cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Change to monthly
              </Typography>
            </Typography>
          </Paper>
        </Box>

        {/* Plan Comparison Header */}
        <Box sx={{ mb: 3, textAlign: 'left' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: '16px', textAlign: 'left' }}>
              Compare plans
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px', textAlign: 'left' }}>
              Find the right plan for you
            </Typography>
          </Box>

          {/* Billing Toggle */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={(_, value) => value && setBillingCycle(value)}
              size="small"
              sx={{
                gap: 0.75,
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.75,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    borderColor: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark
                    },
                    '& .MuiChip-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600
                    }
                  }
                }
              }}
            >
              <ToggleButton value="annually">
                Annual
                <Chip 
                  label="-20%" 
                  size="small" 
                  sx={{ 
                    height: 20,
                    fontSize: '11px',
                    fontWeight: 600,
                    backgroundColor: theme.palette.grey[200],
                    color: theme.palette.text.primary
                  }} 
                />
              </ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Plans Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent',
            overflow: 'visible'
          }}
        >
          <Table sx={{ minWidth: isMobile ? 300 : 900 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                  '& th': {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 2.5,
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'text.secondary'
                  }
                }}
              >
                <TableCell sx={{ width: '30%' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px', color: 'text.primary' }}>
                    Workspace
                  </Typography>
                </TableCell>
                {planTiers.map((tier) => (
                  <TableCell
                    key={tier.id}
                    align="center"
                    sx={{
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      ...(tier.id === currentPlan && {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(59, 130, 246, 0.05)'
                            : 'rgba(59, 130, 246, 0.02)',
                        borderLeft: `2px solid ${theme.palette.primary.main}`,
                        borderRight: `2px solid ${theme.palette.primary.main}`
                      })
                    }}
                  >
                    <Box sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                          {tier.name}
                        </Typography>
                        {tier.popular && (
                          <Chip
                            label="Popular"
                            size="small"
                            color="primary"
                            sx={{ height: 18, fontSize: '10px', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ mb: 0.5, fontSize: '22px' }}
                      >
                        {tier.price === 'custom' ? (
                          'Custom'
                        ) : (
                          <>
                            ${tier.price}
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 400, fontSize: '13px' }}
                            >
                              /mo
                            </Typography>
                          </>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1.5, fontSize: '11px' }}
                      >
                        {tier.billingUnit}
                      </Typography>
                      <Button
                        variant={tier.id === currentPlan ? 'outlined' : tier.popular ? 'contained' : 'outlined'}
                        size="small"
                        fullWidth
                        disabled={tier.id === currentPlan}
                        onClick={() => handlePlanAction(tier.id, tier.buttonAction)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '12px',
                          borderRadius: '6px',
                          py: 0.75,
                          ...(tier.id !== currentPlan && !tier.popular && {
                            borderColor: theme.palette.divider,
                            color: 'text.primary',
                            '&:hover': {
                              borderColor: theme.palette.divider,
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                            }
                          })
                        }}
                      >
                        {tier.buttonText}
                      </Button>
                    </Box>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
              {Array.from(featuresByCategory.entries()).map(
                ([category, features]) => (
                  <React.Fragment key={category}>
                    {/* Category Header */}
                    <TableRow
                      sx={{
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.03)'
                            : 'rgba(0, 0, 0, 0.03)',
                        '& td': {
                          borderTop: `1px solid ${theme.palette.divider}`,
                          py: 1.25
                        }
                      }}
                    >
                      <TableCell colSpan={5}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '13px' }}>
                          {category}
                        </Typography>
                      </TableCell>
                    </TableRow>

                  {/* Features */}
                  {features.map((feature, index) => (
                    <TableRow
                      key={`${category}-${feature.name}`}
                      sx={{
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.015)'
                              : 'rgba(0, 0, 0, 0.01)'
                        },
                        '& td': {
                          py: 1.25,
                          fontSize: '13px'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 400 }}>
                            {feature.name}
                          </Typography>
                          {feature.description && (
                            <Tooltip title={feature.description}>
                              <InfoIcon
                                sx={{
                                  fontSize: 14,
                                  color: 'text.secondary',
                                  cursor: 'help',
                                  opacity: 0.6
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      {planTiers.map((tier) => (
                        <TableCell
                          key={tier.id}
                          align="center"
                          sx={{
                            borderLeft: `1px solid ${theme.palette.divider}`,
                            ...(tier.id === currentPlan && {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(59, 130, 246, 0.05)'
                                  : 'rgba(59, 130, 246, 0.02)',
                              borderLeft: `2px solid ${theme.palette.primary.main}`,
                              borderRight: `2px solid ${theme.palette.primary.main}`
                            })
                          }}
                        >
                          {renderFeatureValue(feature.values[tier.id])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  </React.Fragment>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Plan Change Confirmation Dialog */}
        {selectedPlan && (
          <PlanChangeDialog
            open={changeDialogOpen}
            onClose={() => {
              setChangeDialogOpen(false);
              setSelectedPlan(null);
            }}
            currentPlanId={currentPlan}
            targetPlanId={selectedPlan}
            onConfirm={handleConfirmPlanChange}
          />
        )}

        {/* Support Chat */}
        <SupportChat open={chatOpen} onClose={() => setChatOpen(false)} />
      </Box>
    </Box>
  );
};

